import React, { useRef } from 'react';
import * as CSS from 'csstype';
import styled, { css } from 'styled-components';
import { Desktop } from '@99designs/spa-common';
import { AnnotationContext } from '../../AnnotationContext';
import { useFeedbackModeContext } from '../../FeedbackModeProvider/FeedbackModeProvider';
import { ScribbleContext } from '../../ScribbleContext/ScribbleContext';
import { SelectedEventContext } from '../../SelectedEventContext';
import usePresentationMode from '../../usePresentationMode/usePresentationMode';
import { PointerPositionFn } from '../calculatePosition';
import FeedbackTooltip from './FeedbackTooltip';
import getCursorType from './getCursorType';
import useAnnotationHandlers from './useAnnotationHandlers';
import useHoverHandlers from './useHoverHandlers';
import useScribbleHandlers from './useScribbleHandlers';

interface FeedbackHandlerWrapperProps {
    cursor?: CSS.Property.Cursor;
}
const FeedbackHandlerWrapper = styled.div<FeedbackHandlerWrapperProps>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    ${({ cursor = 'default' }) => css`
        cursor: ${cursor};
    `}
`;

export interface FeedbackHandlerProps<A, S> {
    useSelectedEvent: SelectedEventContext['useSelectedEvent'];
    useAnnotation: AnnotationContext<A>['useAnnotation'];
    useScribble: ScribbleContext<S>['useScribble'];
    pointerPosition: PointerPositionFn;
    children: React.ReactNode;
    fileId: string;
}

function FeedbackHandler<A, S>(props: FeedbackHandlerProps<A, S>) {
    const { pointerPosition, children, useSelectedEvent, useAnnotation, useScribble, fileId } =
        props;
    const { canAnnotate, newAnnotationCoord } = useAnnotation();
    const { canScribble, currentPath } = useScribble();
    const { feedbackMode } = useFeedbackModeContext();
    const { isPresentationMode } = usePresentationMode(); // should this be a prop?

    const ref = useRef<HTMLDivElement>(null);

    const annotationHandlers = useAnnotationHandlers({
        useSelectedEvent,
        useAnnotation,
        ref,
        pointerPosition,
    });
    const scribbleHandlers = useScribbleHandlers<S>({
        ref,
        pointerPosition,
        useScribble,
        fileId,
    });

    const { onClick = undefined } = {
        ...(feedbackMode === 'annotate' && canAnnotate && !isPresentationMode
            ? annotationHandlers
            : {}),
    };
    const { onTouchStart = undefined, onMouseDown = undefined } = {
        ...(feedbackMode === 'sketch' && canScribble && !isPresentationMode
            ? scribbleHandlers
            : {}),
    };

    const { isHovering, onMouseMove, onMouseEnter, onMouseLeave } = useHoverHandlers();
    const showTooltip =
        isHovering && !isPresentationMode && !currentPath && !newAnnotationCoord && canAnnotate;

    return (
        <FeedbackHandlerWrapper
            ref={ref}
            onClick={onClick}
            onTouchStart={onTouchStart}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            cursor={getCursorType(isPresentationMode, feedbackMode, canAnnotate, canScribble)}
        >
            {children}
            {showTooltip && (
                <Desktop>
                    <FeedbackTooltip feedbackMode={feedbackMode} />
                </Desktop>
            )}
        </FeedbackHandlerWrapper>
    );
}

export default FeedbackHandler;
